import React from 'react';
import { Loader2, Check, AlertCircle } from 'lucide-react';
import { CORE_JOURNEY_UI } from '../../config/testerRelease';
import { MEMORY_PIPELINE_LABELS } from '../../content/coreJourneyContent';

/**
 * Visible status near the story editor: upload → queue → transcribe → completed / persistent failure.
 */
export function StoryEditorPipelineStatus({
  isUploadingAudio = false,
  isTranscribing = false,
  transcriptionPhase = 'idle',
  showTranscriptionCompleted = false,
  lastTranscriptionFailure = null,
  onRetry,
  className = '',
}) {
  const base =
    'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors max-w-full';

  if (isUploadingAudio) {
    return (
      <div
        className={`${base} border-border bg-muted/50 text-muted-foreground ${className}`}
        data-testid="pipeline-status-uploading"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
        <span>{CORE_JOURNEY_UI ? MEMORY_PIPELINE_LABELS.uploading : 'Uploading audio…'}</span>
      </div>
    );
  }

  if (isTranscribing) {
    let label = CORE_JOURNEY_UI ? MEMORY_PIPELINE_LABELS.transcribing : 'Transcribing…';
    let testId = 'pipeline-status-transcribing';
    if (transcriptionPhase === 'starting') {
      label = CORE_JOURNEY_UI ? MEMORY_PIPELINE_LABELS.starting : 'Starting transcription…';
      testId = 'pipeline-status-starting';
    } else if (transcriptionPhase === 'queued') {
      label = CORE_JOURNEY_UI ? MEMORY_PIPELINE_LABELS.queued : 'In queue…';
      testId = 'pipeline-status-queued';
    } else if (transcriptionPhase === 'transcribing') {
      label = CORE_JOURNEY_UI ? MEMORY_PIPELINE_LABELS.transcribing : 'Transcribing…';
      testId = 'pipeline-status-transcribing';
    }
    return (
      <div
        className={`${base} border-primary/30 bg-primary/5 text-foreground ${className}`}
        data-testid={testId}
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
        <span>{label}</span>
      </div>
    );
  }

  if (showTranscriptionCompleted) {
    return (
      <div
        className={`${base} border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 ${className}`}
        data-testid="pipeline-status-completed"
      >
        <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>{CORE_JOURNEY_UI ? MEMORY_PIPELINE_LABELS.completed : 'Completed'}</span>
      </div>
    );
  }

  if (lastTranscriptionFailure) {
    return (
      <div
        className={`flex flex-col items-end gap-1.5 sm:items-center sm:flex-row sm:gap-2 ${className}`}
        data-testid="pipeline-status-failed"
        role="alert"
      >
        <div
          className={`${base} border-destructive/40 bg-destructive/5 text-destructive flex-wrap justify-end sm:justify-start`}
        >
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="font-semibold">{CORE_JOURNEY_UI ? MEMORY_PIPELINE_LABELS.failed : 'Transcription failed'}</span>
        </div>
        <p className="text-xs text-muted-foreground text-right sm:text-left max-w-[min(100%,22rem)] leading-snug">
          {lastTranscriptionFailure}
        </p>
        <p className="w-full text-[11px] text-muted-foreground/90 text-right sm:text-left sm:max-w-[min(100%,22rem)]">
          Your recording stays attached to this story. Fix the issue if you can, then retry — your title and draft text
          are not removed.
        </p>
        {onRetry && (
          <button
            type="button"
            className="text-xs font-medium text-primary underline underline-offset-2 shrink-0"
            onClick={onRetry}
            data-testid="pipeline-retry-transcribe"
          >
            {CORE_JOURNEY_UI ? 'Try again' : 'Retry transcription'}
          </button>
        )}
      </div>
    );
  }

  return null;
}
